import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, computed, signal, viewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';

import { GroupChatPayload, UserChat } from '../models/chat.model';
import { MessageCenterService } from '../services';
import { PaginatedResponse } from '@app/models/paginated-response.model';
import { ChatUser } from '../models/user.model';
import { NotificationsService } from '@app/core/services';
import { GroupType } from '@app/core/enums';
import { toBase64 } from '@app/common';
import { IdNameWidgetResponse, IdShortNameWidgetResponse } from '@app/models/response.model';

@UntilDestroy()
@Component({
  selector: 'app-create-group',
  templateUrl: './create-group.component.html',
  styleUrls: ['./create-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CreateGroupComponent implements OnInit {
  avatarInput = viewChild<ElementRef<HTMLInputElement>>('avatarInput');

  form!: FormGroup;
  isLoading = signal(false);
  isSending = signal(false);

  chatId = signal<number | undefined>(undefined);
  loadedAvatar = signal<string | undefined>(undefined);
  
  usersList = signal<ChatUser[]>([]);
  selectedUsers = signal<ChatUser[]>([]);
  allChipsUsers = signal<IdShortNameWidgetResponse[]>([]);
  selectedChipsUsers = signal<IdShortNameWidgetResponse[]>([]);

  membersForAvatarGrid = computed<IdNameWidgetResponse[]>(() =>
    this.selectedUsers().slice(0, 4).map(m => ({ id: m.avatar, name: m.short_name }))
  );

  showMemberGrid = computed(() => this.isEditMode && this.membersForAvatarGrid().length > 0);

  readonly GroupType = GroupType;

  get avatar(): FormControl {
    return this.form?.get('avatar') as FormControl;
  }

  get type(): FormControl {
    return this.form?.get('type') as FormControl;
  }

  get isEditMode(): boolean {
    return !!this.chatId();
  }

  get isPublicType(): boolean {
    return this.form.controls.type?.value === GroupType.Public;
  }

  get isPrivateType(): boolean {
    return this.form.controls.type?.value === GroupType.Private;
  }

  get hasAvatar(): boolean {
    return !!this.form.controls.avatar?.value;
  }

  get avatarHint(): string {
    return this.hasAvatar ? 'messageCenter.customAvatar' : 'messageCenter.uploadAvatarHint';
  }

  constructor(
    private dialogRef: MatDialogRef<CreateGroupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private messageCenterService: MessageCenterService,
    private notificationsService: NotificationsService,
    private cdr: ChangeDetectorRef,
  ) {}


  ngOnInit(): void {
    this.chatId.set(this.data?.chatId);
    this.initForm();

    if (this.chatId()) {
      this.loadExistingGroup(this.chatId()!);
    } else {
      this.loadUsers();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      avatar: [null],
      name: [null, [Validators.required, Validators.maxLength(24)]],
      type: [GroupType.Public, [Validators.required]]
    });
  }

  loadUsers(search?: string): void {
    const params: any = {
      is_widget: true,
      exclude_yourself: 'yes',
      params: search ? 100 : 15
    };

    if (search) {
      params.search = search;
      params.lookup_field = 'full_name';
    }

    this.messageCenterService.getUsersWidget(params)
      .pipe(
        finalize(() => this.cdr.detectChanges()),
        untilDestroyed(this)
      ).subscribe({
        next: (response: PaginatedResponse<ChatUser>) => {
          this.usersList.set(response.results);
          this.allChipsUsers.set(response.results.map(user => ({ id: user.id, short_name: user.short_name })));
        },
        error: (error: HttpErrorResponse) => this.notificationsService.showError(error)
      });
  }

  private loadExistingGroup(chatId: number): void {
    this.isLoading.set(true);
    this.messageCenterService.getUserChatById(chatId)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        untilDestroyed(this)
      ).subscribe({
        next: (response: UserChat) => {
          this.loadedAvatar.set(response.image);
          this.form.patchValue({
            avatar: response.image,
            name: response.name,
            type: response.access_type
          });
          this.selectedUsers.set(response.members ?? []);
          this.selectedChipsUsers.set((response.members ?? []).map((user: ChatUser) => ({ id: user.id, short_name: user.short_name })));
          this.loadUsers();
        },
        error: (error: HttpErrorResponse) => this.notificationsService.showError(error)
      });
  }

  addChipsUser(chip: IdShortNameWidgetResponse): void {
    const user = this.usersList().find(u => u.id === chip.id);
    if (user) {
      this.selectedUsers.update(users => [...users, user])
    };
    this.form.markAsDirty();
  }

  removeChip(chipId: number): void {
    this.selectedUsers.update(users => users.filter(u => u.id !== chipId));
    this.form.markAsDirty();
  }

  createGroup(): void {
    const formValue = this.form.value;
    const payload = {
      ...formValue,
      image: formValue.avatar,
      access_type: +formValue.type,
      members: this.isPrivateType
        ? this.selectedUsers().filter(u => !!u?.id).map(u => u.id)
        : [],
      to_create_members: this.isPrivateType
        ? this.selectedUsers().filter(u => !u?.id).map(u => (u as any).meta)
        : []
    };

    if (this.chatId()) {
      this.updateGroupChat(payload);
    } else {
      this.createGroupChat(payload);
    }
  }

  private updateGroupChat(payload: GroupChatPayload): void {
    if (payload.image === this.loadedAvatar()) {
      delete payload.image;
    }

    this.isSending.set(true);
    this.messageCenterService.updateGroupChat(this.chatId()!, payload)
      .pipe(finalize(() => this.isSending.set(false)), untilDestroyed(this))
      .subscribe({
        next: (response: UserChat) => this.close(response),
        error: (error: HttpErrorResponse) => this.notificationsService.showError(error)
      });
  }

  private createGroupChat(payload: GroupChatPayload): void {
    this.isSending.set(true);
    this.messageCenterService.createGroupChat(payload)
      .pipe(finalize(() => this.isSending.set(false)), untilDestroyed(this))
      .subscribe({
        next: (response: UserChat) => this.close(response),
        error: (error: HttpErrorResponse) => this.notificationsService.showError(error)
      });
  }

  async onAvatarFileChange(event: Event): Promise<void> {
    const files = (event.target as HTMLInputElement).files;
    if (!files?.length) {
      return;
    }

    const base64 = await toBase64(files[0]);
    this.form.patchValue({ avatar: base64 });
    this.cdr.markForCheck();
    this.avatarInput()!.nativeElement.value = '';
  }

  openAvatarUpload(): void {
    this.avatarInput()?.nativeElement.click();
  }

  clearAvatar(): void {
    this.form.patchValue({ avatar: null });
    this.form.markAsDirty();
  }

  groupTypeChanged(value: GroupType): void {
    this.form.controls.type?.setValue(value);
    this.form.controls.type?.markAsDirty();
  }

  isInvalid(): boolean {
    return !this.form?.valid || !this.form.dirty || this.isSending()
      || (this.isPrivateType ? !this.selectedUsers().length : false);
  }

  close(chat: UserChat | null = null): void {
    this.dialogRef.close(chat);
  }
}
